import * as three from "three";
import tailwindColours from "../../../../../../tailwind.colors";
import RectangleMesh from "../../instancing/Meshes/RectangleMesh";
import getMaterialWithUniforms from "../../instancing/materials/getMaterialWithUniforms";
import createArrowTexture from "../../instancing/textures/createArrowTexture";
import createAminoacidTexture from "../../instancing/textures/createAminoacidTexture";
import ArrowLineMesh from "../../instancing/Meshes/ArrowLineMesh";
import ParticleMesh from "../../instancing/Meshes/ParticleMesh";
import { aminoacidsShort } from "../../constants/aminoacids";
import { mainColor, sequenceRgbIndex } from "../../constants/sequences";

const { normal, reversed, rectUvThin, rectUvFat } = createArrowTexture({
  width: 128,
  height: 16,
});

const aminoacids = createAminoacidTexture(
  aminoacidsShort,
  tailwindColours.white,
  mainColor
);

export function getTranscriptsMesh(isLeft) {
  const texture = isLeft ? normal : reversed;
  const conf = { posX: 1, posY: 1, rgbaCode: 1 };
  const matConf = {
    ...texture,
    antialias: true,
    transparent: true,
    blending: three.NormalBlending,
  };
  const materialConf = getMaterialWithUniforms(matConf)
    .setOffset([0, 0])
    .setPaddingX([-0.5, 0.5, -0.5, 0.5])
    .setPaddingY([-4, -4, 4, 4])
    .setColorMap(Array(8).fill(new three.Vector4()))
    .setTexture(texture.texture)
    .setTextureUvMap(rectUvThin);
  return new RectangleMesh(materialConf, conf);
}

export function updateMeshUvMap(mesh, isExonsVisible) {
  const textureUvMap = isExonsVisible ? rectUvThin : rectUvFat;
  mesh.uniforms.setTextureUvMap(textureUvMap);
}

export function getCodonMesh(isLeft) {
  const conf = { posX: 1, posY: 1, raycastInd: 1, rgbaCode: 1 };
  const materialConf = getMaterialWithUniforms({ disableRaycast: false })
    .setOffset([0, 0])
    .setWidth(14)
    .setArrowPointOffset(isLeft ? 5 : -5)
    .setPaddingX([1, -1])
    .setPaddingY([0, 0])
    .setColorMap(Array(8).fill(new three.Vector4()));

  return new ArrowLineMesh(materialConf, conf);
}

export function getCodonTextMesh() {
  const conf = {
    posX: 1,
    posY: 1,
    textureCoordsInd: 1,
  };

  const matconf = {
    textureConf: aminoacids.textureConf,
    disableRaycast: true,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  };

  const materialConf = getMaterialWithUniforms(matconf)
    .setOffset([0, 0])
    .setPointSize(32)
    .setPaddingX([1, -1])
    .setPaddingY([0, 0, 0, 0])
    .setColorMap([new three.Vector4(1, 1, 1, 1)])
    .setTexture(aminoacids.texture)
    .setTextureCoordMap(aminoacids.textureCoords)
    .setTextureUvMap(aminoacids.rectUv);

  return new ParticleMesh(materialConf, conf);
}

export function drawTranscript(transcripts, mesh) {
  const count = transcripts.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const { region, triploHaploIndex } = transcripts[i];
    const { g0, g1 } = region;

    const rInd = sequenceRgbIndex[triploHaploIndex];

    setX(i, g0, g1, g0, g1);
    setRgb(i, rInd, rInd, rInd, rInd);
  }
}

export function updateTranscriptsVis(items, mesh) {
  const setY = mesh.updateY({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const { level } = items[i];
    const y = level + 0.5;
    setY(i, y, y, y, y);
  }
}

export function drawCodon(codons, mesh) {
  const count = codons.length;
  const isAppend = false;
  const setX = mesh.updateX({ count, isAppend });
  const setY = mesh.updateY({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const codon = codons[i];
    const { x0, x1, exon } = codon;
    const { typeIndex } = exon;
    const rInd = sequenceRgbIndex[typeIndex];

    const { region, level } = exon.transcript;
    const { g0 } = region;
    const s = x0 + g0;
    const e = x1 + g0;
    const y = level + 0.5;
    setX(i, s, e);
    setY(i, y, y);
    setRgb(i, rInd, rInd);
  }
}

export function drawCodonText(codons, mesh) {
  const count = codons.length;
  const isAppend = false;
  const setX = mesh.updateX({ count, isAppend });
  const setY = mesh.updateY({ count, isAppend });
  const setTxtCoordInd = mesh.updateTextureCoordsInd({ count, isAppend });

  for (let i = 0; i < count; i++) {
    const codon = codons[i];
    const { x0, exon, size, aminoAcid } = codon;
    const val = size === 3 ? aminoacidsShort[aminoAcid] : aminoAcid;
    const { g0 } = exon.transcript.region;
    const { level } = exon.transcript;
    const y = level + 0.5;
    const s = x0 + g0;
    setX(i, s + size / 2);
    setY(i, y);
    setTxtCoordInd(i, aminoacids.textureCoordMap[val]);
  }
}
